import { useState } from "react";
import {
  createFileRoute,
  useNavigate,
  useRouter,
  useRouterState,
} from "@tanstack/react-router";
import { useForm } from "@tanstack/react-form";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Spinner,
  FormGroup,
  Label,
  Input,
  FormFeedback,
  Button,
  Table,
  Pagination,
  PaginationItem,
  PaginationLink,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "reactstrap";
import Flatpickr from "react-flatpickr";
import { SidePanel } from "@/components";
import "flatpickr/dist/flatpickr.min.css";
import { getCarsMembers } from "@/api/get_cars_members";
import type { CarsMember } from "@/api/get_cars_members";
import {
  getPayrollCategories,
  PayrollCategory,
} from "@/api/get_payroll_categories";
import { getPayrollEntries, PayrollEntry } from "@/api/get_payroll_entries";
import { deletePayrollEntry } from "@/api/delete_payroll_entry";
import {
  createPayrollEntry,
  type CreatePayrollEntryBody,
} from "@/api/create_payroll_entry";
import { downloadPayroll, type PayrollType } from "@/api/payroll_download";
import { capitalize } from "lodash";
import pluralize from "pluralize";
import { format } from "date-fns";

type SearchParams = {
  page: number;
  page_size: number;
};

export const Route = createFileRoute("/payroll/")({
  component: RouteComponent,
  validateSearch: (search: Record<string, unknown>): SearchParams => ({
    page: Number(search.page) || 1,
    page_size: Number(search.page_size) || 20,
  }),
  loaderDeps: ({ search }) => ({ search }),
  loader: async ({ deps }) => {
    const [entries, carsMembers, categories] = await Promise.all([
      getPayrollEntries(deps.search.page, deps.search.page_size),
      getCarsMembers(),
      getPayrollCategories(),
    ]);
    return { entries, carsMembers, categories };
  },
  pendingComponent: () => (
    <div
      className="d-flex justify-content-center align-items-center"
      style={{ height: "50vh" }}
    >
      <Spinner />
    </div>
  ),
});

const payrollOptions: { label: string; type: PayrollType }[] = [
  { label: "Run Payroll for Current Quarter", type: "current" },
  { label: "Run Payroll for Current Fiscal Year", type: "fiscal" },
  { label: "Run Payroll for Previous Quarter", type: "previous" },
];

type SidepanelEntry = {
  descriptionPattern?: RegExp;
  label: string;
  showQuantity?: true;
  useSidepanel: true;
} & PayrollCategory;

const getDescriptionPattern = (name: string): RegExp | undefined => {
  switch (name) {
    case "standby":
    case "calls":
      return /R\d{2}-\d{3}/;
    default:
      return undefined;
  }
};

const getShowQuantity = (name: string) => {
  switch (name) {
    case "admin":
    case "event":
    case "training":
    case "meetings":
      return true;
    default:
      return undefined;
  }
};

const getDescriptionType = (name: string) => {
  switch (name) {
    case "calls":
    case "standby":
      return "text";
    default:
      return "textarea";
  }
};

type AddEntryFormProps = {
  carsMembers: CarsMember[];
  entry: SidepanelEntry;
  onSuccess: () => void;
  showQuantity?: true;
};

function AddEntryForm({
  carsMembers,
  entry,
  onSuccess,
  showQuantity,
}: AddEntryFormProps) {
  const router = useRouter();
  const form = useForm({
    defaultValues: {
      date: new Date(),
      description: "",
      employee_ids: [] as number[],
      quantity: 1,
    },
    onSubmit: async ({ value }) => {
      const body: CreatePayrollEntryBody = {
        date: format(value.date, "yyyy-MM-dd"),
        description: value.description,
        employee_ids: value.employee_ids,
        payroll_category_id: entry.id,
        quantity: value.quantity,
      };
      await createPayrollEntry(body);
      router.invalidate();
      onSuccess();
    },
  });

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        e.stopPropagation();
        form.handleSubmit();
      }}
    >
      <form.Field
        name="date"
        validators={{
          onSubmit: ({ value }) => {
            if (!value) return "Date is required";
            return undefined;
          },
        }}
      >
        {(field) => (
          <FormGroup>
            <Label for="date">Date</Label>
            <Flatpickr
              id="date"
              className={`form-control ${field.state.meta.errors.length > 0 ? "is-invalid" : ""}`}
              value={field.state.value ?? undefined}
              onChange={([selectedDate]) => field.handleChange(selectedDate)}
              options={{ dateFormat: "Y-m-d" }}
              placeholder="Select a date"
            />
            {field.state.meta.errors.length > 0 && (
              <FormFeedback>{field.state.meta.errors[0]}</FormFeedback>
            )}
          </FormGroup>
        )}
      </form.Field>

      {showQuantity && (
        <form.Field
          name="quantity"
          validators={{
            onSubmit: ({ value }) => {
              if (!value || value <= 0) return "Number of hours is required";
              return undefined;
            },
          }}
        >
          {(field) => (
            <FormGroup>
              <Label for="hours">Number of Hours</Label>
              <Input
                id="hours"
                onChange={(e) => field.handleChange(parseFloat(e.target.value))}
                step="0.25"
                type="number"
                value={field.state.value}
                invalid={field.state.meta.errors.length > 0}
              />
              {field.state.meta.errors.length > 0 && (
                <FormFeedback>{field.state.meta.errors[0]}</FormFeedback>
              )}
            </FormGroup>
          )}
        </form.Field>
      )}

      <form.Field
        name="description"
        validators={{
          onChange: ({ value }) => {
            if (
              entry.descriptionPattern &&
              value &&
              !entry.descriptionPattern.test(value)
            ) {
              return "Description does not match required format";
            }
            return undefined;
          },
          onSubmit: ({ value }) => {
            if (!value.trim()) return "Description is required";
            return undefined;
          },
        }}
      >
        {(field) => (
          <FormGroup>
            <Label for="description">Description</Label>
            <Input
              id="description"
              type={getDescriptionType(entry.name)}
              value={field.state.value}
              onChange={(e) => field.handleChange(e.target.value)}
              invalid={field.state.meta.errors.length > 0}
            />
            {field.state.meta.errors.length > 0 && (
              <FormFeedback>{field.state.meta.errors[0]}</FormFeedback>
            )}
          </FormGroup>
        )}
      </form.Field>

      <form.Field
        name="employee_ids"
        validators={{
          onSubmit: ({ value }) => {
            if (value.length === 0) return "At least one person is required";
            return undefined;
          },
        }}
      >
        {(field) => (
          <FormGroup>
            <Label for="employee_ids">People</Label>
            <Input
              id="employee_ids"
              type="select"
              multiple
              value={field.state.value.map(String)}
              onChange={(e) => {
                const select = e.target as unknown as HTMLSelectElement;
                const selected = Array.from(select.selectedOptions, (option) =>
                  Number(option.value),
                );
                field.handleChange(selected);
              }}
              invalid={field.state.meta.errors.length > 0}
            >
              {carsMembers.map((person) => (
                <option key={person.id} value={person.id}>
                  {person.name}
                </option>
              ))}
            </Input>
            {field.state.meta.errors.length > 0 && (
              <FormFeedback>{field.state.meta.errors[0]}</FormFeedback>
            )}
          </FormGroup>
        )}
      </form.Field>

      <Button type="submit" color="primary" className="mt-3 w-100">
        Add {capitalize(pluralize(entry.name, 1))}
      </Button>
    </form>
  );
}

function RouteComponent() {
  const { entries, carsMembers, categories } = Route.useLoaderData();
  const { page, page_size } = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const router = useRouter();
  const isLoading = useRouterState({ select: (s) => s.isLoading });

  const { pagination, items: payroll_entries } = entries;

  const [sidePanelOpen, setSidePanelOpen] = useState(false);
  const [activeEntry, setActiveEntry] = useState<SidepanelEntry | null>(null);
  const [downloading, setDownloading] = useState<PayrollType | null>(null);
  const [entryToDelete, setEntryToDelete] = useState<PayrollEntry | null>(null);
  const [deleting, setDeleting] = useState(false);

  const handlePayrollDownload = async (type: PayrollType) => {
    setDownloading(type);
    try {
      await downloadPayroll(type);
    } catch (error) {
      console.error("Failed to download payroll:", error);
    } finally {
      setDownloading(null);
    }
  };

  const handleDelete = async () => {
    if (!entryToDelete) return;
    setDeleting(true);
    try {
      await deletePayrollEntry({
        employee_id: Number(entryToDelete.employee.id),
        description: entryToDelete.description || "",
      });
      setEntryToDelete(null);
      router.invalidate();
    } finally {
      setDeleting(false);
    }
  };

  const goToPage = (newPage: number) => {
    navigate({
      search: { page: newPage, page_size },
    });
  };

  const changePageSize = (newSize: number) => {
    navigate({
      search: { page: 1, page_size: newSize },
    });
  };

  const pageNumbers = [];
  const maxVisiblePages = 5;
  let startPage = Math.max(1, page - Math.floor(maxVisiblePages / 2));
  const endPage = Math.min(
    pagination.total_pages,
    startPage + maxVisiblePages - 1,
  );
  if (endPage - startPage + 1 < maxVisiblePages) {
    startPage = Math.max(1, endPage - maxVisiblePages + 1);
  }
  for (let i = startPage; i <= endPage; i++) {
    pageNumbers.push(i);
  }

  const toCurrency = (amt: number) => {
    return amt.toLocaleString("en-US", { style: "currency", currency: "USD" });
  };

  const entryOptions: SidepanelEntry[] = categories.items.map((category) => ({
    descriptionPattern: getDescriptionPattern(category.name),
    label: `Add ${capitalize(pluralize(category.name, 1))}`,
    showQuantity: getShowQuantity(category.name),
    useSidepanel: true as const,
    ...category,
  }));

  return (
    <Container className="py-4">
      <Row className="mb-4">
        {payrollOptions.map((option) => (
          <Col key={option.type} xs={12} sm={6} lg={4} className="mb-3">
            <Card
              className="h-100 text-center shadow-sm"
              style={{ cursor: downloading ? "not-allowed" : "pointer" }}
              onClick={() => !downloading && handlePayrollDownload(option.type)}
            >
              <CardBody className="d-flex align-items-center justify-content-center py-3 py-md-4">
                {downloading === option.type ? (
                  <Spinner size="sm" className="me-2" />
                ) : null}
                <h5 className="mb-0 text-primary">{option.label}</h5>
              </CardBody>
            </Card>
          </Col>
        ))}
      </Row>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2 className="mb-0">Payroll Entries</h2>
        <Button color="link" onClick={() => setSidePanelOpen(true)}>
          Add Payroll Entry
        </Button>
      </div>

      <div style={{ position: "relative" }}>
        {isLoading && (
          <div
            className="d-flex justify-content-center align-items-center"
            style={{
              position: "absolute",
              inset: 0,
              backgroundColor: "rgba(255, 255, 255, 0.7)",
              zIndex: 1,
            }}
          >
            <Spinner />
          </div>
        )}
        <Table striped borderless hover responsive className="align-middle">
          <thead>
            <tr>
              <th>Employee</th>
              <th>Type</th>
              <th>Date</th>
              <th>Description</th>
              <th>Cost</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {payroll_entries.length === 0 ? (
              <tr>
                <td colSpan={6} className="text-center text-muted">
                  No payroll entries found
                </td>
              </tr>
            ) : (
              payroll_entries.map((entry, index) => (
                <tr key={index}>
                  <td>{entry.employee.name}</td>
                  <td>{pluralize(capitalize(entry.category.name), 1)}</td>
                  <td>
                    {entry.date
                      ? format(
                          new Date(entry.date + "T00:00:00"),
                          "MMM d, yyyy",
                        )
                      : "-"}
                  </td>
                  <td>{entry.description || "-"}</td>
                  <td>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                      }}
                    >
                      <span>
                        {toCurrency(entry.quantity * entry.category.rate)}
                      </span>
                      <span>
                        ({toCurrency(entry.category.rate)} x {entry.quantity})
                      </span>
                    </div>
                  </td>
                  <td className="text-center">
                    <Button
                      color="link"
                      size="sm"
                      className="text-danger p-0"
                      onClick={() => setEntryToDelete(entry)}
                    >
                      Delete
                    </Button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </Table>
      </div>

      <Row className="align-items-center mt-3">
        <Col xs="auto">
          <div className="d-flex align-items-center gap-2">
            <Label for="pageSize" className="mb-0">
              Rows per page:
            </Label>
            <Input
              id="pageSize"
              type="select"
              value={page_size}
              onChange={(e) => changePageSize(Number(e.target.value))}
              style={{ width: "auto" }}
            >
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </Input>
          </div>
        </Col>
        <Col xs="auto">
          <span className="text-muted">
            Showing {(page - 1) * page_size + 1} -{" "}
            {Math.min(page * page_size, pagination.total)} of {pagination.total}
          </span>
        </Col>
        <Col>
          <Pagination className="mb-0 justify-content-end">
            <PaginationItem disabled={page === 1}>
              <PaginationLink first onClick={() => goToPage(1)} />
            </PaginationItem>
            <PaginationItem disabled={page === 1}>
              <PaginationLink previous onClick={() => goToPage(page - 1)} />
            </PaginationItem>
            {pageNumbers.map((pageNum) => (
              <PaginationItem key={pageNum} active={pageNum === page}>
                <PaginationLink onClick={() => goToPage(pageNum)}>
                  {pageNum}
                </PaginationLink>
              </PaginationItem>
            ))}
            <PaginationItem disabled={page === pagination.total_pages}>
              <PaginationLink next onClick={() => goToPage(page + 1)} />
            </PaginationItem>
            <PaginationItem disabled={page === pagination.total_pages}>
              <PaginationLink
                last
                onClick={() => goToPage(pagination.total_pages)}
              />
            </PaginationItem>
          </Pagination>
        </Col>
      </Row>

      <Modal isOpen={!!entryToDelete} toggle={() => setEntryToDelete(null)}>
        <ModalHeader toggle={() => setEntryToDelete(null)}>
          Confirm Deletion
        </ModalHeader>
        <ModalBody>
          Are you sure you want to delete the payroll entry
          {entryToDelete && (
            <>
              {" "}
              for <strong>{entryToDelete.employee.name}</strong>
              {entryToDelete.description && (
                <> &mdash; {entryToDelete.description}</>
              )}
            </>
          )}
          ?
        </ModalBody>
        <ModalFooter>
          <Button
            color="secondary"
            onClick={() => setEntryToDelete(null)}
            disabled={deleting}
          >
            Cancel
          </Button>
          <Button color="danger" onClick={handleDelete} disabled={deleting}>
            {deleting ? "Deleting..." : "Delete"}
          </Button>
        </ModalFooter>
      </Modal>

      <SidePanel
        isVisible={sidePanelOpen}
        onClose={() => {
          setSidePanelOpen(false);
          setActiveEntry(null);
        }}
        title="Add Payroll Entry"
      >
        <FormGroup>
          <Label for="entryType">Entry Type</Label>
          <Input
            id="entryType"
            type="select"
            value={activeEntry ? String(activeEntry.id) : ""}
            onChange={(e) => {
              const selected = entryOptions.find(
                (opt) => String(opt.id) === e.target.value,
              );
              setActiveEntry(selected ?? null);
            }}
          >
            <option value="" disabled>
              Select a type...
            </option>
            {entryOptions.map((option) => (
              <option key={option.id} value={option.id}>
                {capitalize(pluralize(option.name, 1))}
              </option>
            ))}
          </Input>
        </FormGroup>
        {activeEntry && (
          <AddEntryForm
            carsMembers={carsMembers.items}
            entry={activeEntry}
            onSuccess={() => {
              setSidePanelOpen(false);
              setActiveEntry(null);
            }}
            showQuantity={activeEntry.showQuantity}
          />
        )}
      </SidePanel>
    </Container>
  );
}
